import { TypeAsserter } from '@attain-apps/type-guards'
import { format } from 'pretty-format'

import * as definitions from './definitions'
import type { InstanceHelper } from './helper'
import type { Instance } from './instance'
import { Key } from './key'
import type { Type } from './type'

/**
 * Just a helper type to represent all {@link Instance} in a single interface
 */
export type AllInstances<T = any> = InterfaceToType<
  Satisfies<
    definitions.Date<T> & definitions.Range<T> & definitions.Multirange<T>,
    // NOTE: ensures we don't forget any instances
    Record<Key<Type>, unknown>
  >
>

// NOTE: this type could be more specific, but that causes problems when it is
// being consumed
export const HELPERS_LOOKUP = {
  // NOTE: we use getters since there are circular dependencies, so we need to
  // lazily retrieve each HELPER
  /* eslint-disable jsdoc/require-jsdoc */
  get ['$date']() {
    return definitions.Date.HELPER
  },
  get ['$multirange']() {
    return definitions.Multirange.HELPER
  },
  get ['$range']() {
    return definitions.Range.HELPER
  },
  /* eslint-enable jsdoc/require-jsdoc */
} satisfies {
  [K in Key<Type>]: InstanceHelper<Pick<AllInstances, K>>
} as Readonly<Record<Key, InstanceHelper<Instance>>>

export function getHelper<TInstance extends Instance>(
  instance: TInstance,
): InstanceHelper<TInstance> {
  const key = Key.from(instance)
  TypeAsserter.assertIsNotNullish(
    key,
    `DMS instance is not recognized: ${format(instance)}`,
  )
  // NOTE: casting is needed because TInstance is invariant for InstanceHelper
  return HELPERS_LOOKUP[
    key
  ] satisfies InstanceHelper<Instance> as any as InstanceHelper<TInstance>
}

let helpers: readonly InstanceHelper<Instance>[] | undefined
export function getAllHelpers(): readonly InstanceHelper<Instance>[] {
  return (helpers ??= Object.values(HELPERS_LOOKUP))
}
